// §18 F10 — what a resumable app's window shows while it has no frame.
//
// "Restored from its `session` blob on next focus; **a brief skeleton, never a blank frame**."
// The window is still open the whole time — it kept its place, its bar entry and its blob; only
// the iframe went. So the slot cannot simply be empty, or the user is looking at a window that
// appears to have lost its contents.
//
// A portal into `.win-content`, exactly like `AppErrorLayer`, for the same §5.7 reason: the host
// element is created once and removed only by the close.
import { createPortal } from 'react-dom';
import { COPY } from '@/copy';
import { useKernel } from '@/kernel/react';
import type { HostSlots } from './AppHost';

export interface AppSkeletonLayerProps {
  /** Instance key → app id, for every window whose frame is currently gone. */
  suspended: ReadonlyMap<string, string>;
  slots: ReadonlyMap<string, HostSlots>;
}

function AppSkeleton({ appId }: { appId: string }) {
  const kernel = useKernel();
  return (
    <div className="app-skeleton" data-testid={`app-skeleton-${appId}`} aria-busy="true">
      <span
        className="app-skeleton-icon"
        aria-hidden
        dangerouslySetInnerHTML={{ __html: kernel.appIconSvg(appId, 48) }}
      />
      <p className="app-skeleton-message">{COPY.appResuming}</p>
      <div className="app-skeleton-lines" aria-hidden>
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

export function AppSkeletonLayer({ suspended, slots }: AppSkeletonLayerProps) {
  return (
    <>
      {[...suspended].map(([instanceKey, appId]) => {
        const slot = slots.get(instanceKey);
        if (!slot) return null;
        return createPortal(<AppSkeleton appId={appId} />, slot.content, instanceKey);
      })}
    </>
  );
}
